import {
  isExternal,
  postDayStatus,
  liveShiftViews,
  postLiveParts,
  staffs,
  type LivePart,
  type LiveShiftView,
  type LiveStatus,
} from '@reditelji/shared';
import { seenIn, type ShiftCheckins } from '../live/derive';
import type { ChangelogEntry, PoolVolunteer, Shift, TimelinePost } from '../api/types';
import { awaitsConfirmation, briefingStale, shiftConfirmation } from '../timeline/derive';

/** Pin/legend status keys for the Zemljevid screen. */
export type MapStatusKey = 'ok' | 'part' | 'gap' | 'none' | 'brief' | 'off' | 'ekipa';

export const MAP_COL: Record<MapStatusKey, string> = {
  ok: 'oklch(0.58 0.1 155)',
  part: 'oklch(0.68 0.11 75)',
  gap: 'oklch(0.57 0.15 25)',
  none: 'oklch(0.7 0.012 95)',
  brief: 'oklch(0.55 0.1 245)',
  off: 'oklch(0.62 0.02 300)',
  // Not a pin colour of its own: the swatch the legend draws for the square
  // that rides on top of whatever status the post already has (SPEC §1.1J).
  ekipa: 'oklch(0.62 0.012 95)',
};

export const MAP_LEGEND: [MapStatusKey, string][] = [
  ['ok', 'polno pokrito in potrjeno'],
  ['part', 'čaka potrditev'],
  ['gap', 'vrzel ali odpoved'],
  ['none', 'brez izmen'],
  ['brief', 'navodila še niso poslana'],
  ['ekipa', 'zunanja ekipa'],
];

export type VolunteerLookup = Map<string, PoolVolunteer>;

/** A shift actually holding its slot (open slots never do). */
function isStaffed(s: Shift): boolean {
  return staffs(s);
}

/**
 * Is an answer still owed on this shift? Asked of the shift, as the board asks
 * it (SPEC §1.1F): a team, somebody not coming and a finished shift owe none.
 */
function isPending(s: Shift, vols: VolunteerLookup): boolean {
  return awaitsConfirmation(s, s.volunteerId ? vols.get(s.volunteerId) : undefined);
}

/** Whether this volunteer id belongs to an external team (SPEC §1.1J). */
const externalOf = (vols: VolunteerLookup) => (id: string) => {
  const v = vols.get(id);
  return v !== undefined && isExternal(v);
};

/**
 * Does an outside group work this post? Drawn as a square on the pin.
 *
 * With `atMin` the question is asked of that minute only, so the time slider's
 * marker moves with its colour instead of describing the whole day.
 */
export function externalOn(p: TimelinePost, vols: VolunteerLookup, atMin?: number): boolean {
  const on = atMin === undefined ? p.shifts : p.shifts.filter((s) => s.fromMin <= atMin && atMin < s.toMin);
  return on.some((s) => isStaffed(s) && externalOf(vols)(s.volunteerId!));
}

/**
 * Whole-day status of a post. Coverage is checked against the post's real
 * operating windows (the timeline's source of truth) rather than the shift
 * extent; a shift still owed an answer, or refused, leaves the post waiting.
 */
export function statusOf(p: TimelinePost, vols: VolunteerLookup): MapStatusKey {
  return postDayStatus(
    p.shifts,
    p.windows,
    // Refused or unanswered, the post waits either way; nobody else is asked.
    (s) => (isPending(s, vols) ? 'čaka' : 'potrjen'),
    externalOf(vols),
  ).status;
}

export interface TimeStatus {
  key: MapStatusKey;
  label: string;
}

/** Status of a post at minute `m` (time-slider mode). */
export function atTime(p: TimelinePost, m: number, vols: VolunteerLookup): TimeStatus {
  const on = p.shifts.filter((s) => s.fromMin <= m && m < s.toMin);
  if (!on.length) return { key: 'off', label: 'ni izmene' };
  const staffed = on.filter(isStaffed);
  if (!staffed.length) return { key: 'gap', label: 'PROSTO' };
  const names = staffed
    .map((s) => vols.get(s.volunteerId!)?.name ?? '?')
    .join(', ');
  return { key: staffed.some((s) => isPending(s, vols)) ? 'part' : 'ok', label: names };
}

/**
 * A shift agreed on this post whose holder's briefing was never sent or is
 * stale — the detail row's ✉ asks the same, so a finished shift, whose row
 * carries none, is left out here too.
 */
export function briefMissing(p: TimelinePost, vols: VolunteerLookup): boolean {
  return p.shifts.some((s) => {
    if (!s.volunteerId || s.state === 'open' || s.state === 'done') return false;
    const v = vols.get(s.volunteerId);
    // An external team has no briefing that could be missing.
    if (v === undefined || isExternal(v)) return false;
    return shiftConfirmation(s, v) === 'potrjen' && briefingStale(v);
  });
}

export type ChangeKind = 'time' | 'people' | 'instr' | 'photo' | 'cancel';

export interface PostChange {
  kind: ChangeKind;
  breaking: boolean;
  at: Date;
}

const CHANGE_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

function changeKindOf(e: ChangelogEntry): ChangeKind {
  if (e.kind === 'deleted') return 'cancel';
  // Photo events are logged against the post, like an edit; without this they
  // would read as "navodila dopolnjena", which is not what happened.
  if (e.kind.startsWith('photo')) return 'photo';
  const after = (e.after ?? {}) as Record<string, unknown>;
  if (e.entity === 'shift') {
    // A "Ne pride" frees the slot and its undo hands it back (SPEC §1.1K):
    // who stands there changed, not when or whether the post runs.
    if (e.kind === 'freed' || e.kind === 'restored') return 'people';
    if (after.state === 'cancelled') return 'cancel';
    if ('volunteerId' in after) return 'people';
    if ('fromMin' in after || 'toMin' in after || 'meetMin' in after) return 'time';
    return 'people';
  }
  if (e.entity === 'post_window') return 'time';
  return 'instr';
}

/**
 * Recently-changed dot per post, derived from the audit log:
 * the latest shift/post/window entry touching the post within the last 3 days.
 */
export function recentChangesByPost(
  entries: ChangelogEntry[],
  posts: TimelinePost[],
  now: Date,
): Map<string, PostChange> {
  const postIds = new Set(posts.map((p) => p.id));
  const shiftToPost = new Map<string, string>();
  for (const p of posts) for (const s of p.shifts) shiftToPost.set(s.id, p.id);

  const out = new Map<string, PostChange>();
  // entries arrive newest-first; keep the first hit per post
  for (const e of entries) {
    const at = new Date(e.createdAt);
    if (now.getTime() - at.getTime() > CHANGE_WINDOW_MS) continue;
    let postId: string | null = null;
    if ((e.entity === 'post' || e.entity === 'post_window') && e.entityId && postIds.has(e.entityId))
      postId = e.entityId;
    else if (e.entity === 'shift' && e.entityId) {
      postId = shiftToPost.get(e.entityId) ?? null;
      if (!postId) {
        const snap = (e.after ?? e.before ?? {}) as Record<string, unknown>;
        if (typeof snap.postId === 'string' && postIds.has(snap.postId)) postId = snap.postId;
      }
    }
    if (!postId || out.has(postId)) continue;
    out.set(postId, { kind: changeKindOf(e), breaking: e.breaking, at });
  }
  return out;
}

const CHANGE_LABEL: Record<ChangeKind, string> = {
  time: 'ura izmene spremenjena',
  people: 'zamenjava redarja',
  instr: 'navodila dopolnjena',
  photo: 'fotografije dopolnjene',
  cancel: 'odpoved izmene',
};

export function changeLabel(c: PostChange, now: Date): string {
  const days = Math.floor((now.getTime() - c.at.getTime()) / (24 * 60 * 60 * 1000));
  const when = days === 0 ? 'danes' : days === 1 ? 'včeraj' : `pred ${days} dnevi`;
  const hm = `${c.at.getHours()}:${String(c.at.getMinutes()).padStart(2, '0')}`;
  return `${CHANGE_LABEL[c.kind]} · ${when}, ${hm}`;
}

// ---------------------------------------------------------------------------
// Race day (SPEC §1.2): who stands at each post now
// ---------------------------------------------------------------------------

export const LIVE_COL: Record<LiveStatus, string> = {
  kasneje: 'var(--rr-live-kasneje)',
  kmalu: 'var(--rr-live-kmalu)',
  na: 'var(--rr-live-na)',
  ni: 'var(--rr-live-ni)',
  zamuja: 'var(--rr-live-zamuja)',
  prosto: 'var(--rr-live-prosto)',
  brez: 'var(--rr-live-brez)',
  odjavljen: 'var(--rr-live-odjavljen)',
  niodj: 'var(--rr-live-odjavljen)',
  ekipa: 'var(--rr-live-ekipa)',
};

/** The legend on race day, worst first; `niodj` shares a row with `odjavljen`. */
export const LIVE_LEGEND: [LiveStatus, string][] = [
  ['prosto', 'PROSTO — nihče ni dodeljen'],
  ['zamuja', 'zamuja 15+ min'],
  ['brez', 'ni prišel (izmena mimo)'],
  ['ni', 'ni prijave (0–15 min)'],
  ['kmalu', 'izmena v naslednjih 45 min'],
  ['na', 'na mestu'],
  ['ekipa', 'zunanja ekipa (brez prijav)'],
  ['odjavljen', 'odjavljen'],
  ['kasneje', 'izmena kasneje danes'],
];

/** The legend row a status is counted and hidden under. */
export const legendKey = (st: LiveStatus): LiveStatus => (st === 'niodj' ? 'odjavljen' : st);

/** How each shift at this post reads within its person's stretch (`liveShiftViews`). */
export function liveViewsOf(p: TimelinePost, cm: Map<string, ShiftCheckins>, now: number): Map<string, LiveShiftView> {
  return liveShiftViews(p.shifts, seenIn(cm), now);
}

/** The people who matter at this post now, worst first. */
export function livePartsOf(
  p: TimelinePost,
  cm: Map<string, ShiftCheckins>,
  vols: VolunteerLookup,
  now: number,
): LivePart<Shift>[] {
  return postLiveParts(
    p.shifts,
    seenIn(cm),
    now,
    (s) => {
      const v = s.volunteerId ? vols.get(s.volunteerId) : undefined;
      return v !== undefined && isExternal(v);
    },
  );
}

/**
 * A pin's fill: one slice per person who matters now, worst first from the
 * top — a handover reads half green, half yellow. A slot with nobody on it is
 * a pale slice, with the pin's dashed red outline saying why.
 */
export function pinFill(parts: LivePart<Shift>[]): string {
  // Nobody matters here any more today: pale, not the grey of a shift to come.
  if (parts.length === 0) return 'oklch(0.94 0.004 95)';
  const colour = (st: LiveStatus) => (st === 'prosto' ? 'oklch(0.96 0.03 25)' : LIVE_COL[st]);
  if (parts.length === 1) return colour(parts[0]!.status);
  const step = 360 / parts.length;
  const stops = parts.map((p, i) => `${colour(p.status)} ${i * step}deg ${(i + 1) * step}deg`);
  // The pin body is turned -45deg, so the gradient starts from its tip's opposite.
  return `conic-gradient(from 45deg, ${stops.join(', ')})`;
}
